import {
  Box, Container, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useEffect, useMemo, useState } from 'react';
import { useLogo } from '../../../config/useLogo';
import { DashboardScreensLayoutProps } from '../DashboardLayout';
import { selectShowDashboardToolbar } from '../../../store/slices/dashboardSlice';
import { useAppSelector } from '../../../store/hooks';
import { DashboardNavElement } from '../DashboardNavElement';
import { MobileMenuDialog } from './MobileMenuDialog';

export function DashboardMobileLayout({
  children,
  navElems,
}: DashboardScreensLayoutProps) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { secondaryOne: NavLogo } = useLogo();
  const showDashboardToolbar = useAppSelector(selectShowDashboardToolbar);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const updateHeight = () => setWindowHeight(window.innerHeight);
    window?.addEventListener('resize', updateHeight);
    return () => window?.removeEventListener('resize', updateHeight);
  }, []);

  const maxMenuItemListHeight = useMemo(() => `${windowHeight * 0.7 - 80}px`, [windowHeight]);

  const DrawerList = (
    <>
      <Box
        sx={{
          overflowY: 'auto',
          maxHeight: maxMenuItemListHeight,
          // Custom scrollbar styling for better visibility
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[200],
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette.grey[500],
            },
          },
        }}>
        {
          navElems.filter(((item) => !item.bottomFixPosition)).map((elem) => (
            <DashboardNavElement
              sx={{
                marginBottom: theme.spacing(1.25),
                padding: theme.spacing(1.875),
              }}
              key={elem.name}
              name={elem.name}
              path={elem.path}
              outlineIcon={elem.Outline}
              filledIcon={elem.Filled}
              isSelected={elem.isCurrent ?? false}
              onClick={() => {
                if (elem.onClick) elem.onClick();
                setOpen(false);
              }}
              subMenuItems={elem.subMenuItems}
              bottomFixPosition={elem.bottomFixPosition}
              secondaryLabel={elem.secondaryLabel}
              rightIcon={elem.rightIcon}
              onSecondaryLabelClick={elem.onSecondaryLabelClick}
            />
          ))
        }
      </Box>
      <Box>
        {
          navElems.filter(((item) => item.bottomFixPosition)).map((elem) => (
            <DashboardNavElement
              sx={{
                marginBottom: theme.spacing(1.25),
                padding: theme.spacing(1.875),
              }}
              key={elem.name}
              name={elem.name}
              path={elem.path}
              outlineIcon={elem.Outline}
              filledIcon={elem.Filled}
              isSelected={elem.isCurrent ?? false}
              onClick={() => {
                if (elem.onClick) elem.onClick();
                setOpen(false);
              }}
              subMenuItems={elem.subMenuItems}
              bottomFixPosition={elem.bottomFixPosition}
              secondaryLabel={elem.secondaryLabel}
              rightIcon={elem.rightIcon}
              onSecondaryLabelClick={elem.onSecondaryLabelClick}
            />
          ))
        }
      </Box>
    </>
  );

  return (
    <>
      <Box sx={{
        height: '100dvh',
        overflowY: 'scroll',
        backgroundColor: theme.palette.common.white,
      }}>
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
            boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.12),0px 0px 4px 0px rgba(0, 0, 0, 0.14)',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1201,
          }}
        >
          <Box sx={[{
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: theme.spacing(1.5),
            height: theme.spacing(6),
            width: theme.spacing(6),
            padding: theme.spacing(1),
          }, { ...(open && { background: theme.palette.grey[300] }) }]}
          id="optimus-mobile-menu-icon-parent"
          >
            <MenuIcon sx={[{
              width: theme.spacing(4),
              height: theme.spacing(4),
              cursor: 'pointer',
              color: theme.palette.grey[500],
              fontWeight: 500,
            }, { ...(open && { color: theme.palette.brand.primary }) }]}
            onClick={() => setOpen(!open)}
            id="optimus-mobile-menu-icon"
            />
          </Box>
          {NavLogo && <img
            src={NavLogo as string}
            alt="Logo"
            height={34}
            style={{
              marginTop: theme.spacing(3),
              marginBottom: theme.spacing(3),
              marginLeft: theme.spacing(1.375),
              width: theme.spacing(19.75),
            }}
          />}
        </Box>
        <MobileMenuDialog open={open} onClose={() => setOpen(false)}>
          <Box
            sx={{
              pt: theme.spacing(13),
              pr: theme.spacing(1.25),
              pl: theme.spacing(1.25),
            }}
          >
            {DrawerList}
          </Box>
        </MobileMenuDialog>
        <Container id='mobile-layout-container' maxWidth={showDashboardToolbar ? 'lg' : 'md'}
          sx={{
            p: '0px !important',
            [theme.breakpoints.down('md')]: {
              padding: `${theme.spacing(2)} !important`,
            },
          }}
        >
          {children}
        </Container>
      </Box>
    </>
  );
}
